import type { HeliosRecordDataTablesData } from 'types/dataHelios';
import type { HeliosCellData, HeliosCellRawValue, HeliosColumnsCellData } from 'types/helios/agendaListing';
import type { runHeliosHegFunctionRes } from 'types/helios/runFunctions';
import { getHeliosRecordDetailUrl } from 'utils/navigation/urls';
import { restoreKey } from 'utils/formItemsDots';

const isStringRecord = (value: unknown): value is Record<string, string> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.entries(value).every(([, v]) => typeof v === 'string');
};

const isHeliosCellData = (value: unknown): value is HeliosCellData =>
  typeof value === 'object' && value !== null && 'rawValue' in value && 'columnType' in value && 'recordNumber' in value;

const cellRawValueToString = (rawValue: HeliosCellRawValue): string | undefined => {
  if (rawValue === null || rawValue === '') return undefined;
  if (typeof rawValue === 'string') return rawValue;
  if (typeof rawValue === 'number') return String(rawValue);
  if (typeof rawValue === 'object' && 'id' in rawValue) return String(rawValue.id);
  return undefined;
};

export const getJoinColumnFiltersFromState = (state: unknown): Record<string, string> | undefined => {
  if (typeof state !== 'object' || state === null || !('joinColumnFilters' in state)) return undefined;
  const { joinColumnFilters } = state;
  return isStringRecord(joinColumnFilters) ? joinColumnFilters : undefined;
};

export const buildJoinColumnFiltersFromItems = (items: HeliosRecordDataTablesData['items']): Record<string, string> => {
  const filters: Record<string, string> = {};
  for (const item of items) {
    if (item.rawValue !== '' && item.columnType !== 'RelationDynamic') {
      filters[item.columnKey] = item.rawValue;
    }
  }
  return filters;
};

export const buildJoinColumnFiltersFromRow = (row: HeliosColumnsCellData): Record<string, string> => {
  const filters: Record<string, string> = {};
  for (const [key, value] of Object.entries(row)) {
    if (key === 'id' || key === 'identifier') continue;
    if (!isHeliosCellData(value)) continue;
    if (value.columnType === 'RelationDynamic') continue;
    const rawStr = cellRawValueToString(value.rawValue);
    if (rawStr === undefined) continue;
    const originalKey = restoreKey(key);
    filters[originalKey] = rawStr;
  }
  return filters;
};

export const getHeliosCreatedRecordUrl = (
  resultData: runHeliosHegFunctionRes | undefined,
  moduleId: string | undefined,
  fallbackTemplateId: string,
  isWorkflow: boolean
): string | null => {
  if (
    isWorkflow ||
    !moduleId ||
    !resultData?.openCreatedRecord ||
    !resultData?.createdRecordNumbers ||
    resultData.createdRecordNumbers.length === 0
  ) {
    return null;
  }
  const targetTemplateId = resultData.openCreatedRecordTemplateId || fallbackTemplateId;
  const recordNumber = resultData.createdRecordNumbers[0];
  return getHeliosRecordDetailUrl(moduleId, targetTemplateId, recordNumber);
};
